import { create } from 'zustand';

export type PortalMode = 'token' | 'grant';

interface PortalState {
  /**
   * Token mode: the raw X-Access-Token. Grant mode: a non-empty sentinel marking an active
   * reviewer session (the real Clerk bearer token is fetched fresh per-request — see
   * setReviewerTokenProvider in @edison/api-client-react — never cached here).
   */
  token: string | null;
  /** 'token' = anonymous access-token portal (unchanged). 'grant' = authenticated external
   *  reviewer portal (Group B), scoped to `claimId` via Clerk auth + X-Claim-Id. */
  mode: PortalMode;
  /** The claim currently selected by a grant-mode reviewer. Always null in token mode. */
  claimId: string | null;
  setToken: (token: string) => void;
  setGrantSession: (claimId: string) => void;
  clearToken: () => void;
}

/**
 * Portal store — holds the current audit-portal session (anonymous X-Access-Token OR
 * authenticated reviewer grant). Kept in memory only (Zustand state), not in localStorage —
 * scoped to the current browser tab session. Lost on refresh by design (mirrors the anonymous
 * token portal's existing behavior); a grant-mode reviewer who refreshes is routed back to
 * /reviewer to re-pick their claim (their Clerk session itself persists across refresh).
 */
export const usePortalStore = create<PortalState>((set) => ({
  token: null,
  mode: 'token',
  claimId: null,
  setToken: (token) => set({ token, mode: 'token', claimId: null }),
  setGrantSession: (claimId) => set({ token: 'grant-session', mode: 'grant', claimId }),
  clearToken: () => set({ token: null, mode: 'token', claimId: null }),
}));
